import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  House, 
  Users,
  Files,
  SignOut,
  List,
  X,
  At,
  FolderOpen,
  GearSix,
  ClockCounterClockwise,
  UserGear,
  ShieldCheck
} from '@phosphor-icons/react';
import { useState } from 'react';
import IndieLawLogo from './IndieLawLogo';

export default function AdminLayout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navItems = [
    { path: '/admin', label: 'Dashboard', icon: House },
    { path: '/admin/clients', label: 'Clients', icon: Users },
    { path: '/admin/matters', label: 'Matters', icon: Files },
    { path: '/admin/monitoring', label: 'Monitoring', icon: ShieldCheck },
    { path: '/admin/coverage', label: 'Coverage', icon: UserGear },
    { path: '/admin/team', label: 'Team', icon: UserGear },
    { path: '/admin/history', label: 'History', icon: ClockCounterClockwise }
  ];

  const isActive = (path) => {
    if (path === '/admin') {
      return location.pathname === '/admin';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-[#F4F4F5]">
      {/* Top Navigation */}
      <header className="sticky top-0 z-50" style={{ backgroundColor: '#141b4d' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/admin" className="flex items-center gap-3" data-testid="admin-logo-link">
              <IndieLawLogo variant="light" />
              <span className="ml-2 text-xs font-medium uppercase tracking-wider" style={{ color: '#ffd100' }}>Admin</span>
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-1">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors ${
                    isActive(item.path)
                      ? 'text-white bg-white/10'
                      : 'text-white/70 hover:text-white hover:bg-white/5'
                  }`}
                  style={isActive(item.path) ? { color: '#ffd100' } : {}}
                  data-testid={`admin-nav-${item.label.toLowerCase()}`}
                >
                  <item.icon weight={isActive(item.path) ? 'duotone' : 'regular'} className="w-5 h-5" />
                  {item.label}
                </Link>
              ))}
            </nav>

            {/* User Info & Logout */}
            <div className="flex items-center gap-4">
              <div className="hidden sm:block text-right">
                <p className="text-sm font-medium text-white">{user?.name}</p>
                <p className="text-xs text-white/50">{user?.email}</p>
              </div>
              <Link to="/admin/settings" className="p-2 text-white/70 hover:text-white hover:bg-white/10 transition-colors"
                title="Settings" data-testid="admin-settings-link">
                <GearSix weight="regular" className="w-5 h-5" />
              </Link>
              <button
                onClick={logout}
                className="p-2 text-white/70 hover:text-white hover:bg-white/10 transition-colors"
                title="Sign out"
                data-testid="admin-logout-btn"
              >
                <SignOut weight="regular" className="w-5 h-5" />
              </button>
              
              {/* Mobile menu button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden p-2 text-white/70 hover:text-white"
                data-testid="admin-mobile-menu-btn"
              >
                {mobileMenuOpen ? (
                  <X weight="bold" className="w-6 h-6" />
                ) : (
                  <List weight="bold" className="w-6 h-6" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Nav */}
        {mobileMenuOpen && (
          <nav className="md:hidden border-t border-white/10" style={{ backgroundColor: '#141b4d' }}>
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setMobileMenuOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 text-sm font-medium border-b border-white/10 ${
                  isActive(item.path)
                    ? 'bg-white/10'
                    : 'text-white/70'
                }`}
                style={isActive(item.path) ? { color: '#ffd100' } : {}}
              >
                <item.icon weight={isActive(item.path) ? 'duotone' : 'regular'} className="w-5 h-5" />
                {item.label}
              </Link>
            ))}
          </nav>
        )}
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {children}
      </main>
    </div>
  );
}
